import { useCallback, useMemo } from "react";
import type { TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { compareScenarioRows, hasConfigurationDifferences } from "./evalCompareDiffModel";
import {
  buildSeedStabilityCsv,
  mergedSummaryCsvFilename,
  policyCompareSummaryCsvFilename,
} from "./evalCompareCsvModel";
import { shortRunName, type CompareRun, type EvalResultSeries } from "./evalCompareModel";
import { buildMergedResult, type EvalMergeExclusion } from "./evalCompareMergeModel";
import { buildGroupCompareResult, buildGroupPlaceholderSeries } from "./evalGroupCompareModel";
import type { EvalCompareGroup } from "./policyCompareGroups";
import type { BaselineSeries } from "../evalBaselineModel";
import type { EvalCompareController } from "./useEvalCompareController";

export type EvaluationComparisonMode = "compare" | "merge" | "group";

const MODE_CONFIG = {
  compare: {
    title: "Evaluation 비교",
    progressLabel: "비교 중",
    pooledNoun: "비교 결과",
    includeSampleCount: false,
    showRunSublabels: false,
    showDurations: true,
    resultsNeedPooling: false,
  },
  merge: {
    title: "Evaluation 통합",
    progressLabel: "통합 중",
    pooledNoun: "통합 결과",
    includeSampleCount: true,
    showRunSublabels: false,
    showDurations: false,
    resultsNeedPooling: true,
  },
  group: {
    title: "Policy Evaluation 비교",
    progressLabel: "비교 중",
    pooledNoun: "Policy 별 집계",
    includeSampleCount: true,
    showRunSublabels: true,
    showDurations: false,
    resultsNeedPooling: true,
  },
} as const;

type ViewerNoticeItem = { label: string; detail?: string };
type ViewerNotice = {
  key: string;
  tone: "warning" | "info";
  message: string;
  items?: readonly ViewerNoticeItem[];
};

const NO_GROUPS: readonly EvalCompareGroup[] = [];

export function useEvaluationComparisonViewModel({
  controller,
  onOpenRunDetail,
  mode,
  groups,
}: {
  controller: EvalCompareController;
  onOpenRunDetail: (run: TrainingEvalRunSummaryView) => void;
  mode: EvaluationComparisonMode;
  groups?: readonly EvalCompareGroup[];
}) {
  const { compareRuns, loading, loadFailed, termsFailed, mergeReady, retry } = controller;
  const config = MODE_CONFIG[mode];
  const groupList = mode === "group" ? (groups ?? NO_GROUPS) : null;
  const mergedResult = useMemo(
    () => (mode === "merge" && mergeReady ? buildMergedResult(compareRuns) : null),
    [compareRuns, mergeReady, mode],
  );
  const groupInputs = useMemo(() => {
    if (groupList === null) return null;
    const groupIndexByRunId = new Map<string, number>();
    groupList.forEach((group, index) => {
      for (const run of group.runs) groupIndexByRunId.set(run.id, index);
    });
    const buckets = groupList.map((group) => ({
      key: group.key,
      label: group.label,
      totalRuns: group.totalRuns,
      items: [] as CompareRun[],
    }));
    for (const item of compareRuns) {
      const index = groupIndexByRunId.get(item.key);
      if (index !== undefined) buckets[index].items.push(item);
    }
    return buckets;
  }, [groupList, compareRuns]);
  const groupResult = useMemo(
    () => (groupInputs !== null && mergeReady ? buildGroupCompareResult(groupInputs) : null),
    [groupInputs, mergeReady],
  );
  const userResultSeries = useMemo(
    () =>
      groupResult !== null
        ? groupResult.series
        : mergedResult !== null
          ? [mergedResult.series]
          : config.resultsNeedPooling
            ? []
            : compareRuns,
    [groupResult, mergedResult, config.resultsNeedPooling, compareRuns],
  );
  // 요약 표는 병합 열을 왼쪽에 모은다. baseline 바로 옆에 통합 결과가 오고, 개별 run 이 그 뒤에 온다.
  const userSummarySeries = useMemo(
    () =>
      mergedResult !== null
        ? [mergedResult.series, ...mergedResult.sourceSeries]
        : userResultSeries,
    [mergedResult, userResultSeries],
  );
  const baselineSeries = controller.baseline.series;
  // baseline 은 항상 왼쪽 끝 열이다. 합친 배열은 memo 로 유지한다. 표·차트의 useMemo 와 recharts 는
  // 배열 identity 로 재계산 여부를 정한다.
  const withBaseline = useCallback(
    <T extends EvalResultSeries>(items: readonly T[]): readonly (T | BaselineSeries)[] =>
      baselineSeries === null ? items : [baselineSeries, ...items],
    [baselineSeries],
  );
  const resultSeries = useMemo(
    () => withBaseline(userResultSeries),
    [withBaseline, userResultSeries],
  );
  const summarySeries = useMemo(
    () => withBaseline(userSummarySeries),
    [withBaseline, userSummarySeries],
  );
  const hasDistributions = resultSeries.some((item) => item.summary.distributions.length > 0);
  const hasOutcomes = userResultSeries.some((item) => item.summary.outcomeBreakdown.length > 0);
  const hasHeadlineMetrics =
    !config.resultsNeedPooling ||
    resultSeries.some((item) => item.summary.finalSuccessRate !== null);
  // 구성 표와 소요 시간 차트가 함께 쓰는 열이다. 사용자 쪽은 pooling 전 run 을 그대로 쓴다.
  // 두 표의 memo 는 compareRuns 의 안정된 identity 에 의존한다. 그래서 여기서도 memo 로 유지한다.
  const configurationRuns = useMemo(() => withBaseline(compareRuns), [withBaseline, compareRuns]);
  const hasScenarioData = compareScenarioRows(configurationRuns).length > 0;
  const labelByRunId = useMemo(
    () => Object.fromEntries(compareRuns.map((item) => [item.key, item.label])),
    [compareRuns],
  );
  const openRunByKey = useCallback(
    (runId: string) => {
      const target = compareRuns.find((item) => item.key === runId);
      if (target !== undefined) onOpenRunDetail(target.run.summaryRow);
    },
    [compareRuns, onOpenRunDetail],
  );
  const configurationDiffers = mode === "merge" && hasConfigurationDifferences(compareRuns);
  const heterogeneousGroupLabels = useMemo(
    () =>
      groupInputs === null || !mergeReady || loading
        ? []
        : groupInputs
            .filter((input) => input.items.length > 0 && hasConfigurationDifferences(input.items))
            .map((input) => input.label),
    [groupInputs, mergeReady, loading],
  );
  const truncatedGroupItems = useMemo<readonly ViewerNoticeItem[]>(
    () =>
      groupList === null
        ? []
        : groupList
            .filter((group) => group.totalRuns > group.runs.length)
            .map((group) => ({
              label: group.label,
              detail: `${group.totalRuns}개 중 최근 ${group.runs.length}개`,
            })),
    [groupList],
  );
  const allPoliciesEmpty =
    groupList !== null && compareRuns.length === 0 && !loading && !loadFailed;
  const groupLegendItems: readonly EvalResultSeries[] | null = useMemo(() => {
    if (groupList === null) return null;
    if (groupResult !== null) return groupResult.series;
    return buildGroupPlaceholderSeries(
      groupList.map((group) => ({
        key: group.key,
        label: group.label,
        runCount: group.runs.length,
        totalRuns: group.totalRuns,
      })),
    );
  }, [groupList, groupResult]);
  const showMergedResults =
    !config.resultsNeedPooling || mergedResult !== null || groupResult !== null;
  const progressText =
    groupList !== null
      ? `Policy ${groupList.length}개 비교 중 · 실행 ${compareRuns.length}개`
      : `실행 ${compareRuns.length}개 ${config.progressLabel}`;

  const notices: ViewerNotice[] = [...controller.baseline.notices];
  if (configurationDiffers) {
    notices.push({
      key: "merge-heterogeneous",
      tone: "warning",
      message:
        "선택한 실행의 Policy 또는 Scenario 설정이 서로 다릅니다. 통합 결과는 서로 다른 구성을 한데 합친 값이니 유의해 주세요.",
    });
  }
  if (truncatedGroupItems.length > 0) {
    notices.push({
      key: "truncated-policies",
      tone: "warning",
      message: "성공 실행이 많은 Policy 는 최근 실행만 집계했습니다.",
      items: truncatedGroupItems,
    });
  }
  if (heterogeneousGroupLabels.length > 0) {
    notices.push({
      key: "group-heterogeneous",
      tone: "warning",
      message: "다음 Policy 는 설정(Policy 또는 Scenario)이 서로 다른 실행을 함께 집계했습니다.",
      items: heterogeneousGroupLabels.map((label) => ({ label })),
    });
  }
  if (groupResult !== null && groupResult.seedSetsDiffer) {
    notices.push({
      key: "seed-sets-differ",
      tone: "warning",
      message:
        "Policy 마다 집계에 쓰인 Seed 집합이 서로 달라, 같은 조건에서 잰 성공률이 아닙니다. 비교할 때 유의해 주세요.",
      items: groupResult.groups
        .filter((group) => group.retainedSeeds.length > 0)
        .map((group) => ({ label: group.label, detail: `Seed ${group.retainedSeeds.join(", ")}` })),
    });
  }
  // 통합 결과가 어느 session 의 실행을 합친 값인지 표에서 바로 알 수 있어야 한다.
  const mergedSessions = useMemo(() => {
    if (mergedResult === null) return [];
    const byId = new Map<string, { id: string; name: string }>();
    for (const item of mergedResult.includedItems) {
      const id = item.run.summaryRow.source_session_id;
      if (id === null || byId.has(id)) continue;
      byId.set(id, { id, name: item.run.summaryRow.source_session_name ?? shortRunName(id) });
    }
    return [...byId.values()];
  }, [mergedResult]);

  if (mergedSessions.length > 0) {
    notices.push({
      key: "merge-sessions",
      tone: "info",
      message: `통합 결과는 Baseline 을 제외한 ${mergedSessions
        .map((session) => session.name)
        .join(", ")} 의 결과를 합친 값입니다.`,
    });
  }
  if (mergedResult !== null && mergedResult.exclusions.length > 0) {
    notices.push({
      key: "merge-exclusions",
      tone: "warning",
      message: "다음 실행은 통합·Seed 통계에서 뺐습니다.",
      items: mergeExclusionItems(mergedResult.exclusions, labelByRunId),
    });
  }
  if (groupResult !== null) {
    const excluded = groupResult.groups.filter((group) => group.merged.exclusions.length > 0);
    if (excluded.length > 0) {
      notices.push({
        key: "group-exclusions",
        tone: "warning",
        message: "다음 Policy 는 일부 실행을 집계에서 뺐습니다.",
        items: excluded.map((group) => ({
          label: group.label,
          detail: groupExclusionDetail(group.merged.exclusions),
        })),
      });
    }
  }

  return {
    allPoliciesEmpty,
    baselineSeries,
    mergedSessions,
    baselineLoading: controller.baseline.status === "loading",
    compareRuns,
    configurationRuns,
    config,
    exportAppendix: mergedResult === null ? undefined : buildSeedStabilityCsv(mergedResult),
    exportFilename:
      groupResult !== null
        ? policyCompareSummaryCsvFilename(groupResult.groups.length)
        : mergedResult !== null
          ? mergedSummaryCsvFilename(mergedResult.selectedRunCount)
          : undefined,
    groupLegendItems,
    hasDistributions,
    hasHeadlineMetrics,
    hasOutcomes,
    hasScenarioData,
    loadFailed,
    loading,
    mergedResult,
    notices,
    openRunByKey,
    progressText,
    resultSeries,
    retry,
    showMergedResults,
    summarySeries,
    termsFailed,
  };
}

function groupExclusionDetail(exclusions: readonly EvalMergeExclusion[]): string {
  const countOf = (kind: EvalMergeExclusion["kind"]) =>
    exclusions.filter((exclusion) => exclusion.kind === kind).length;
  const duplicates = countOf("duplicate_seed");
  const missing = countOf("missing_seed");
  const unavailable = countOf("terms_unavailable");
  return [
    duplicates > 0 ? `Seed 중복 ${duplicates}개(Seed 당 하나만 집계)` : null,
    missing > 0 ? `Seed 없음 ${missing}개` : null,
    unavailable > 0 ? `success term 불러오기 실패 ${unavailable}개(다시 시도하면 포함)` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(", ");
}

function mergeExclusionItems(
  exclusions: readonly EvalMergeExclusion[],
  labelByRunId: Readonly<Record<string, string>>,
): ViewerNoticeItem[] {
  const nameOf = (runId: string) => labelByRunId[runId] ?? "이름 없는 Policy";
  const duplicateLabels = exclusions.flatMap((exclusion) =>
    exclusion.kind === "duplicate_seed"
      ? [`Seed ${exclusion.seed} (${nameOf(exclusion.runId)})`]
      : [],
  );
  const namesOf = (kind: EvalMergeExclusion["kind"]) =>
    exclusions
      .filter((exclusion) => exclusion.kind === kind)
      .map((exclusion) => nameOf(exclusion.runId));
  const missingLabels = namesOf("missing_seed");
  const unavailableLabels = namesOf("terms_unavailable");
  const items: ViewerNoticeItem[] = [];
  if (duplicateLabels.length > 0) {
    items.push({
      label: "Seed 중복",
      detail: `${duplicateLabels.join(", ")} (Seed 당 실행 하나만 통합)`,
    });
  }
  if (missingLabels.length > 0)
    items.push({ label: "Seed 없음", detail: missingLabels.join(", ") });
  if (unavailableLabels.length > 0) {
    items.push({
      label: "success term 불러오기 실패",
      detail: `${unavailableLabels.join(", ")} (다시 시도하면 포함)`,
    });
  }
  return items;
}
